import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useFlagsStatus } from '@unleash/proxy-client-react';
import { type FC, useCallback, useContext, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { makePortalUserNotificationsChannel, SocketEvent } from '@furality/centrifugo-types';
import { ReportError, useAuth, useQuery } from '@furality/frontend-stack';
import { Button, ToastEnum } from '@furality/furality-purpose';
import fox from '#/api/fox.ts';
import FontAwesomeIcon from '#/components/FontAwesomeIcon.tsx';
import { ScrollToTopButton } from '#/components/ScrollToTopStickyButton.tsx';
import StickyButtonStack from '#/components/StickyButtonStack.tsx';
import {
  FriendLocationContext,
  organizeFriendsByEvent,
} from '@/components/__app/FriendLocationProvider.tsx';
import EmptyFriendsListCard from '@/components/_friends/EmptyFriendListCard.tsx';
import FriendsInfoModal from '@/components/_friends/FriendsInfoModal.tsx';
import FriendsModal from '@/components/_friends/FriendsModal.tsx';
import HiddenLocationCard from '@/components/_friends/HiddenLocationCard.tsx';
import LocationShareToggleButton from '@/components/_friends/LocationShareToggleButton.tsx';
import NoFriendsOnline from '@/components/_friends/NoFriendsOnlineCard.tsx';
import SyncButton from '@/components/_friends/SyncButton.tsx';
import WorldCard from '@/components/_friends/WorldCard.tsx';
import { FullLayout } from '@/components/layouts/index.tsx';
import PageLoading from '@/components/loading/PageLoading.tsx';
import PageDisabled from '@/components/PageDisabled.tsx';
import RefreshButton from '@/components/RefreshButton.tsx';
import TooltipHelper from '@/components/TooltipHelper.tsx';
import type { FriendStreamLocation } from '@/lib/db/friendLocation.db.ts';
import useChannelEventSubscription from '@/lib/hooks/useChannelEventSubscription';
import useFlag from '@/lib/hooks/useFlag.ts';
import QUERY_KEYS from '@/lib/QUERY_KEYS.ts';
import useToasts from '@/lib/useToast.ts';

const FriendsInternal: FC = () => {
  const toasts = useToasts();
  const { t } = useTranslation(['common', 'friends']);
  const auth = useAuth();

  const [showFriendsListIsEmptyInfoModal, setShowFriendsListIsEmptyInfoModal] = useState(false);
  const [showFriendsModal, setShowFriendsModal] = useState(false);
  const [showFriendsInfoModal, setShowFriendsInfoModal] = useState(false);
  const [modalFriends, setModalFriends] = useState<FriendStreamLocation[]>([]);
  const { useActiveFriends, reloadConnection } = useContext(FriendLocationContext);

  const featuresReady = useFlagsStatus().flagsReady;
  const enableFriends = useFlag('friends');

  // Total friend count always returns the true amount of friends, even when hiding location from friends.
  const totalFriendCount = useQuery({
    queryKey: [QUERY_KEYS.Friends.Count],
    queryFn: async () => {
      const response = await fox.value.friendGetCount();
      switch (response.status) {
        case 200:
          return response.data;
        default:
          throw new ReportError(response);
      }
    },
    enabled: enableFriends,
  });

  const activeFriends = useActiveFriends();
  const friendEvents = organizeFriendsByEvent(activeFriends ?? []);

  const qc = useQueryClient();

  const refreshFriendLocations = useCallback(() => {
    qc.resetQueries({ queryKey: [QUERY_KEYS.EventInstances] });
    reloadConnection();
  }, [qc, reloadConnection]);

  const visibility = useQuery({
    queryKey: [QUERY_KEYS.Friends.Visibility],
    queryFn: async () => {
      const response = await fox.value.userGetVisibility();
      switch (response.status) {
        case 200:
          return response.data;
        case 404:
          return null;
        default:
          throw new ReportError(response);
      }
    },
  });
  const isLocationHidden = visibility.data && visibility.data.visibility === 'private';

  const sortedFriendWorlds = useMemo(
    () => friendEvents.toSorted((a, b) => a.eventId.localeCompare(b.eventId)),
    [friendEvents],
  );

  // TODO: this should be a component
  const friendsContent = useMemo(() => {
    /* Friend list isn't added yet. */
    if (totalFriendCount.isSuccess && totalFriendCount.data.friendCount === 0) {
      return <EmptyFriendsListCard onClick={() => setShowFriendsListIsEmptyInfoModal(true)} />;
    }

    /* Note: friends CAN appear when location is hidden from friends IF the user is a team member */
    /* So that's why this first case doesn't check against isLocationHidden.*/
    if (sortedFriendWorlds.length > 0) {
      // Sort events by friends count
      return (
        <div className="mt-4 d-flex flex-wrap justify-content-center align-items-stretch gap-3">
          {sortedFriendWorlds.map((data) => (
            <WorldCard
              className="mb-0 max80ch flex-grow-1"
              key={data.eventId}
              friends={data.friends}
              eventId={data.eventId}
              setFriends={(friends) => {
                setModalFriends(friends);
                setShowFriendsModal(true);
              }}
            />
          ))}
        </div>
      );
    }

    /*
     * If the backend isn't returning any active friends (but the user does have friends added)
     * then one of these cases might be true:
     *   1. User has location hidden.
     *   2. No one is online/mutual
     */
    if (
      totalFriendCount.isSuccess &&
      totalFriendCount.data.friendCount > 0 &&
      activeFriends?.length === 0
    ) {
      return isLocationHidden ? (
        <HiddenLocationCard />
      ) : (
        <NoFriendsOnline setShowFriendsInfoModal={setShowFriendsInfoModal} className="mt-5" />
      );
    }

    return null;
  }, [
    activeFriends?.length,
    sortedFriendWorlds,
    isLocationHidden,
    totalFriendCount.data,
    totalFriendCount.isSuccess,
  ]);

  const channel = makePortalUserNotificationsChannel(auth.user?.id ?? '');
  useChannelEventSubscription(
    channel,
    SocketEvent.UserVrchatFriendsSynced,
    () => {
      void totalFriendCount.refetch();
      toasts.pushToast(ToastEnum.Info, t('friends:friends.sync_socket.success.toast'));
    },
    { enabled: !!auth.user?.id },
  );

  useChannelEventSubscription(
    channel,
    SocketEvent.UserVrchatFriendsSyncError,
    () => {
      toasts.pushToast('error', t('friends:friends.sync_socket.error.toast'));
    },
    { enabled: !!auth.user?.id },
  );

  if (!featuresReady || activeFriends === undefined) {
    return <PageLoading />;
  }

  if (!enableFriends) {
    return <PageDisabled />;
  }

  return (
    <div className="mt-4 mt-xl-6 mb-md-4">
      <FriendsInfoModal onHide={() => setShowFriendsInfoModal(false)} show={showFriendsInfoModal} />
      <FriendsInfoModal
        hideNoFriendsOnline
        onHide={() => setShowFriendsListIsEmptyInfoModal(false)}
        show={showFriendsListIsEmptyInfoModal}
      />
      <FriendsModal
        show={showFriendsModal}
        onHide={() => setShowFriendsModal(false)}
        modalFriends={modalFriends}
      />
      <Row className="justify-content-center row-gap-3">
        <Col md="auto" xs={12}>
          <h1 className="m-0 text-center">{t('friend_locations')}</h1>
        </Col>
        {totalFriendCount.isSuccess && (
          <Col xs="auto">
            <RefreshButton
              variant="dark"
              onClick={refreshFriendLocations}
              tooltipId="refresh-friend-locations"
              tooltip={t('friends:friends.refresh_friend_locations')}
            />
          </Col>
        )}
        <Col xs="auto">
          <LocationShareToggleButton />
        </Col>
        <Col xs="auto" className="ml-lg-auto">
          <Row className="justify-content-center row-gap-3">
            <Col xs="auto">
              <TooltipHelper
                id="learn-more"
                title={t('friends:friends.learn_more')}
                child={
                  <Button
                    icon={<FontAwesomeIcon icon={faQuestionCircle} />}
                    aria-label={t('friends:friends.learn_more')}
                    variant="dark"
                    onClick={() => setShowFriendsInfoModal(true)}
                  >
                    {t('friends:help')}
                  </Button>
                }
              />
            </Col>
            <Col xs="auto" className="text-center">
              <SyncButton />
            </Col>
          </Row>
        </Col>
      </Row>

      <div>{friendsContent}</div>

      <StickyButtonStack>
        {totalFriendCount.isSuccess && (
          <RefreshButton
            size="xl"
            iconSize="2x"
            variant="primary"
            onClick={refreshFriendLocations}
            tooltipId="refresh-friend-locations-sticky"
            tooltip={t('friends:friends.refresh_friend_locations')}
          />
        )}
        <ScrollToTopButton />
      </StickyButtonStack>
    </div>
  );
};

export const Route = createFileRoute('/friends')({
  component: () => (
    <FullLayout title="shared.navigation.friends">
      <FriendsInternal />
    </FullLayout>
  ),
});
